import { MouseEvent, KeyboardEventHandler, memo, useCallback } from "react";
import { TableCellRendererProps } from "@vuu-ui/vuu-table-types";
import { Checkbox } from "@salt-ds/core";
import { isEditRowReadOnly } from "@vuu-ui/vuu-data-editing";
import {
  dataColumnAndKeyUnchanged,
  dispatchCustomEvent,
  isDataValueEditable,
  isRpcSuccess,
  registerComponent,
} from "@vuu-ui/vuu-utils";
import { useComponentCssInjection } from "@salt-ds/styles";
import { useWindow } from "@salt-ds/window";

import checkboxCellCss from "./CheckboxCell.css";

const classBase = "vuuCheckboxCell";

export const CheckboxCell = memo(
  ({ column, dataRow, onEdit }: TableCellRendererProps) => {
    const targetWindow = useWindow();
    useComponentCssInjection({
      testId: "vuu-checkbox-cell",
      css: checkboxCellCss,
      window: targetWindow,
    });

    const isChecked = !!dataRow[column.name];
    const readOnly = isEditRowReadOnly(dataRow);

    const handleCommit = useCallback(
      (value: boolean) => async (evt: MouseEvent) => {
        const res = await onEdit?.(
          { previousValue: isChecked, value },
          "commit",
        );
        if (isRpcSuccess(res)) {
          dispatchCustomEvent(evt.target as HTMLElement, "vuu-commit");
        }
        return res;
      },
      [isChecked, onEdit],
    );

    const handleKeyDown = useCallback<KeyboardEventHandler>(
      async (evt) => {
        if (evt.key === "Enter") {
          const res = await onEdit?.(
            { previousValue: isChecked, value: !isChecked },
            "commit",
          );
          if (isRpcSuccess(res)) {
            dispatchCustomEvent(evt.target as HTMLElement, "vuu-commit");
          }
        }
      },
      [isChecked, onEdit],
    );

    const className = `${classBase}-checkbox`;

    return isDataValueEditable(column, "update") ? (
      <Checkbox
        checked={isChecked}
        className={className}
        disabled={readOnly}
        onClick={handleCommit(!isChecked)}
        onKeyDown={handleKeyDown}
      />
    ) : (
      <Checkbox checked={isChecked} className={className} disabled={true} />
    );
  },
  dataColumnAndKeyUnchanged,
);
CheckboxCell.displayName = "CheckboxCell";

registerComponent("checkbox-cell", CheckboxCell, "cell-renderer", {
  serverDataType: "boolean",
});
